import {
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { StyledTableCell, StyledTableHeader } from "./baseTable.style";

export interface IBaseColumnTable extends TableCellProps {
  key?: string;
  columnTitle?: string;
  maxLines?: number;
  node?: boolean;
  style?: React.CSSProperties;
  render: (dataItem: any, dataIndex?: number) => any;
}

export type IBaseTableProps =
  | IServerPaginatorTableProps
  | IClientPaginatorTableProps;

interface IServerPaginatorTableProps extends TableProps {
  serverPaginator: true;
  totalRecords: number;
  onPageChange: ({
    oldPage,
    newPage,
  }: {
    oldPage: number;
    newPage: number;
  }) => void;
  onPageSizeChange: ({
    oldPage,
    newPage,
  }: {
    oldPage: number;
    newPage: number;
  }) => void;
}

interface IClientPaginatorTableProps extends TableProps {
  serverPaginator: false;
}

interface TableProps {
  isLoading?: boolean;
  rowsPerPageOptions?: (
    | number
    | {
        value: number;
        label: string;
      }
  )[];
  tableConfigs: IBaseColumnTable[];
  data: any[];
  page?: number;
  colorHeader?: boolean;
  size?: "small" | "medium";
  component: React.ElementType<any>;
  showPagination?: boolean;
  showPaninationAll?: boolean;
  locale: {
    all: string;
    backIconButtonText: string;
    labelRowsPerPage: string;
    labelDisplayedRows: ({
      from,
      to,
      count,
    }: {
      from: number;
      to: number;
      count: number;
    }) => string;
    nextIconButtonText: string;
    noData: string;
  };
}

export default function BaseTable(props: IBaseTableProps) {
  const {
    tableConfigs,
    serverPaginator,
    data,
    rowsPerPageOptions,
    showPagination,
    showPaninationAll,
    isLoading,
    size,
    component,
    colorHeader,
    locale,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [displayData, setDisplayData] = useState([] as any[]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (serverPaginator || !showPagination) {
      setDisplayData(data);
    } else {
      const displayData = data.slice(
        page * rowsPerPage,
        (page + 1) * rowsPerPage
      );
      if (page > Math.ceil(data.length / rowsPerPage) - 1) {
        setPage(0);
      }
      setDisplayData(displayData);
    }
  }, [data, page, rowsPerPage, serverPaginator, showPagination]);

  useEffect(() => {
    if (!data.length) {
      setPage(0);
    }
  }, [data]);

  if (showPaninationAll && showPagination) {
    rowsPerPageOptions?.push({ value: -1, label: locale.all });
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <TableContainer component={component}>
        <Table size={size}>
          <StyledTableHeader colorheader={colorHeader?.toString()}>
            <TableRow>
              {tableConfigs.map((col, index) => (
                <TableCell key={"header-" + index} component="th">
                  {col.columnTitle}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHeader>
          <TableBody>
            {displayData.map((item, index) => (
              <TableRow key={"row-" + index} sx={{ height: '50px'}} id="tbl-admin_table-row">
                {tableConfigs.map((col: IBaseColumnTable, colIndex: number) => (
                  <TableCell
                    key={`cell-${index}-${colIndex}`}
                    scope="row"
                    style={col.style}
                    id={`dat-${col.id || colIndex}-cell`}
                  >
                    {col?.node ? (
                      col.render(item, index)
                    ) : (
                      <StyledTableCell maxLines={col.maxLines}>
                        {col.render(item, index)}
                      </StyledTableCell>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!isLoading && displayData.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={tableConfigs.length}>
                  {locale.noData}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {showPagination && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={
                    serverPaginator
                      ? (props as IServerPaginatorTableProps).totalRecords
                      : data.length
                  }
                  page={page}
                  colSpan={tableConfigs.length + 1}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={locale.labelRowsPerPage}
                  labelDisplayedRows={locale.labelDisplayedRows}
                  getItemAriaLabel={(type) => {
                    const types = {
                      first: locale.backIconButtonText,
                      previous: locale.backIconButtonText,
                      next: locale.nextIconButtonText,
                      last: locale.nextIconButtonText,
                    }
                    return types[type];
                  }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </>
  );
}

export const defaultProps: IBaseTableProps = {
  isLoading: false,
  serverPaginator: false,
  showPagination: true,
  component: "div",
  showPaninationAll: false,
  colorHeader: true,
  tableConfigs: [],
  data: [],
  size: "medium",
  rowsPerPageOptions: [5, 10, 25],
  locale: {
    all: "All",
    backIconButtonText: "Previous Page",
    labelRowsPerPage: "Rows per page:",
    labelDisplayedRows: ({
      from,
      to,
      count,
    }: {
      from: number;
      to: number;
      count: number;
    }) => `${from}-${to} of ${count}`,
    nextIconButtonText: "Next Page",
    noData: "No data to display",
  },
};

BaseTable.defaultProps = defaultProps;
